import { useCallback, useRef } from "react";

/**
 * Window-bounds choreographer for the desktop shell's three focus modes.
 *
 * The Electron shell supports a four-mode shell-focus cycle:
 *   Mode 0 — sidebar + chat (+ optional terminal)
 *   Mode 1 — sidebar + terminal (chat hidden)
 *   Mode 2 — terminal only (sidebar + chat hidden)
 * (Mode 3 in App.tsx is the cycle-back transition; bounds-wise it's mode 0.)
 *
 * Entering a focus mode ≥1 shrinks the OS window from the left so the
 * remaining UI stays put — chat collapsing makes the window narrower by the
 * chat's width, sidebar collapsing makes it narrower by the sidebar's width
 * on top of that. Returning to mode 0 restores the original bounds.
 *
 * Geometry capture is delicate because Electron's `getWindowBounds` returns
 * outer-window pixels but DOM `getBoundingClientRect` returns CSS-pixel
 * content space; the scale factor between them is `bounds.width /
 * bounds.contentWidth`. We measure both panes against the live DOM at
 * collapse time and stash everything (saved bounds + per-pane widths) on
 * `preCollapseGeometryRef` so subsequent collapses/restores can shrink and
 * grow without re-measuring.
 *
 * The hook is a no-op on web (no `window.__electronApi`).
 *
 * Returns an async `resizeWindowForFocusLevel(fromLevel, toLevel)` callback
 * that App.tsx invokes from `handleCycleViewMode` and `handleToggleTerminal`.
 *
 * @param terminalWidth - Current terminal pane width (CSS px). Read as a
 *   fallback when the DOM measurement of `[data-ai-terminal-pane]` fails
 *   (e.g. terminal mounted but not yet visible).
 */
type WindowBounds = { x: number; y: number; width: number; height: number };
type WindowBoundsExt = WindowBounds & {
  contentWidth: number;
  contentHeight: number;
};
type WindowBoundsWithWidths = WindowBounds & {
  chromeX: number; // outer width - content width
  sidebarWidth: number; // in outer-bounds scale
  chatWidth: number;
  terminalWidth: number;
};

export function useShellFocusResizer(terminalWidth: number) {
  const preCollapseGeometryRef = useRef<{
    width: number;
    height: number;
    x: number;
    y: number;
  } | null>(null);

  const getElectronWindowApi = useCallback(() => {
    const w = window as unknown as {
      __electronApi?: {
        getWindowBounds?: () => Promise<WindowBoundsExt | null>;
        setWindowBounds?: (bounds: WindowBounds) => Promise<void>;
        setWindowMinSize?: (w: number, h: number) => Promise<void>;
        resetWindowMinSize?: () => Promise<void>;
        setWindowResizable?: (resizable: boolean) => Promise<void>;
      };
    };
    const api = w.__electronApi;
    if (api?.getWindowBounds && api?.setWindowBounds) {
      return {
        getWindowBounds: api.getWindowBounds,
        setWindowBounds: api.setWindowBounds,
        setWindowMinSize: api.setWindowMinSize,
        resetWindowMinSize: api.resetWindowMinSize,
        setWindowResizable: api.setWindowResizable,
      };
    }
    return null;
  }, []);

  const resizeWindowForFocusLevel = useCallback(
    async (fromLevel: 0 | 1 | 2, toLevel: 0 | 1 | 2) => {
      const api = getElectronWindowApi();
      if (!api) return;

      if (fromLevel === 0 && toLevel > 0) {
        // Save original bounds and measure DOM element widths.
        // getBounds returns outer window size, getContentSize returns
        // CSS-pixel content size. The difference is window chrome.
        // Scale DOM measurements (CSS px) to outer-bounds space.
        const bounds = await api.getWindowBounds();
        if (!bounds) return;
        const chromeX = bounds.width - bounds.contentWidth;
        const scale =
          bounds.contentWidth > 0 ? bounds.width / bounds.contentWidth : 1;
        const sidebar = document.querySelector("[data-sidebar-surface]");
        const terminal = document.querySelector("[data-ai-terminal-pane]");
        const sidebarW = (sidebar?.getBoundingClientRect().width ?? 0) * scale;
        const terminalW =
          (terminal?.getBoundingClientRect().width ?? terminalWidth) * scale;
        const chatW = Math.max(
          0,
          bounds.width - chromeX - sidebarW - terminalW,
        );
        preCollapseGeometryRef.current = {
          ...bounds,
          chromeX,
          sidebarWidth: sidebarW,
          chatWidth: chatW,
          terminalWidth: terminalW,
        } as WindowBoundsWithWidths;
      }

      const saved =
        preCollapseGeometryRef.current as WindowBoundsWithWidths | null;
      if (!saved) {
        return;
      }

      if (toLevel === 0) {
        // Restoring to full — restore min size, then bounds
        preCollapseGeometryRef.current = null;
        await api.resetWindowMinSize?.();
        await api.setWindowBounds({
          x: saved.x,
          y: saved.y,
          width: saved.width,
          height: saved.height,
        });
      } else {
        // Calculate how much to remove from the left
        let shrinkBy = 0;
        if (toLevel >= 1) shrinkBy += saved.chatWidth;
        if (toLevel >= 2) shrinkBy += saved.sidebarWidth;
        const newWidth = saved.width - shrinkBy;
        // Lower min size so the window can actually shrink
        await api.setWindowMinSize?.(Math.max(100, newWidth), 200);
        await api.setWindowBounds({
          x: saved.x + shrinkBy,
          y: saved.y,
          width: newWidth,
          height: saved.height,
        });
      }
    },
    [getElectronWindowApi, terminalWidth],
  );

  return resizeWindowForFocusLevel;
}
